import { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { eventDate as formatEventDate, eventTime, extractDate, extractTime } from '../../helper';
import useBootstrapCollapse from '../../util/useBootstrapCollapse';
import { Translate, useTranslation } from '../../config/TranslationProvider';
import { useInfiniteEventDates } from '../../api/hooks';
import EventProblemBadge from '../card/EventProblemBadge';

const FirstEventDateInfo = ({startDate, endDate, sameDayEventDates}) => {
    const clock = useTranslation('clock');
    const newDate = useTranslation('newDate');
    const fullDay = useTranslation('full_day');

    return (
        <>
            <ul style={{marginBottom: 20}}>
                <li className="atbd_open">
                    <span style={{fontWeight: 700}}>
                        <Translate value="date" />
                    </span>
                </li>
                <li className="atbd_open">
                    <span>{formatEventDate(startDate, endDate)}</span>
                </li>
            </ul>

            <ul>
                <li className="atbd_open">
                    <span style={{fontWeight: 700}}>
                        {sameDayEventDates.length === 1
                            ? <Translate value="appointment" />
                            : <Translate value="appointments" />
                        }
                    </span>
                </li>

                {Object.values(sameDayEventDates).map((eventDate, key) =>
                    <ul key={eventDate.id}>
                        <li className={`atbd_open ${eventDate.is_rescheduled ? 'styles-removed' : ''}`}>
                            <span className="day lh-24 text-left">
                                {extractTime(eventDate.start_date) === extractTime(eventDate.end_date) ?
                                    <Translate value="full_day" />
                                    :
                                    `${eventTime(eventDate.start_date, eventDate.end_date)} ${clock}`
                                }
                            </span>
                            {eventDate.is_cancelled && (
                                <EventProblemBadge type="cancelled" />
                            )}
                            {eventDate.is_rescheduled && (
                                <EventProblemBadge type="rescheduled" />
                            )}
                        </li>
                        {eventDate.is_rescheduled && (
                            <li className="default-event-list-element mt-1">
                                <span className="rescheduled-color text-left">
                                    {eventDate.is_rescheduled && eventDate.rescheduled_start_date && <>
                                        {newDate}
                                        {formatEventDate(eventDate.rescheduled_start_date, eventDate.rescheduled_end_date)}
                                        {' '}
                                        {extractTime(eventDate.rescheduled_start_date) === extractTime(eventDate.rescheduled_end_date)
                                            ? fullDay
                                            : `${eventTime(eventDate.rescheduled_start_date, eventDate.rescheduled_end_date)} ${clock}`
                                        }
                                    </>}
                                    {eventDate.is_rescheduled && !eventDate.rescheduled_start_date && (
                                        <Translate value="newDateUnknown" />
                                    )}
                                </span>
                            </li>
                        )}
                    </ul>
                )}
            </ul>
        </>
    );
};


const OtherDayEventDates = ({ otherDayEventDates }) => {
    const {collapseRef, collapseSizeRef, showCollapse, toggleCollapse, collapseClassNames} = useBootstrapCollapse();

    const clock = useTranslation('clock');
    const newDate = useTranslation('newDate');
    const fullDay = useTranslation('full_day');
    return <>
        <button
            className="btn btn-secondary"
            type="button"
            aria-expanded={showCollapse ? 'true' : 'false'}
            aria-controls="later-events"
            onClick={toggleCollapse}
        >
            <Translate value="events_later" count={otherDayEventDates.length} />
        </button>
        <div
            id="later-events"
            ref={collapseRef}
            className={collapseClassNames}
            style={{
                scrollMarginTop: '26rem',
            }}
        >
            <div className="directory_open_hours" ref={collapseSizeRef}>
                <ul className="other-day-event" style={{
                    maxHeight: '34rem',
                    overflowY: 'auto',
                }}>
                    {Object.values(otherDayEventDates).map((eventDate, key) => (
                        <li key={eventDate.id}>
                            <ul style={{ width: '100%' }}>
                                <li className={classNames({
                                    'atbd_open': true,
                                    'styles-removed': (
                                        eventDate.is_rescheduled
                                        || (eventDate.is_cancelled && key === (otherDayEventDates.length - 1))
                                    ),
                                })} style={{ gap: '0' }}>
                                    <span className="day lh-24 text-left">
                                        {extractTime(eventDate.start_date) === extractTime(eventDate.end_date)
                                            ? formatEventDate(eventDate.start_date, eventDate.end_date) + ', ' + fullDay
                                            : (
                                                formatEventDate(eventDate.start_date, eventDate.end_date)
                                                + ', '
                                                + eventTime(eventDate.start_date, eventDate.end_date)
                                                + ' '
                                                + clock
                                            )
                                        }
                                    </span>
                                    {eventDate.is_cancelled && <EventProblemBadge type="cancelled" />}
                                    {eventDate.is_rescheduled && <EventProblemBadge type="rescheduled" />}
                                </li>
                                {eventDate.is_rescheduled && (
                                    <li className={key === (otherDayEventDates.length - 1)
                                        ? 'styles-removed mt-1'
                                        : ''
                                    }>
                                        <span className="rescheduled-color text-left">
                                            {eventDate.is_rescheduled && eventDate.rescheduled_start_date && <>
                                                {newDate}
                                                {formatEventDate(eventDate.rescheduled_start_date, eventDate.rescheduled_end_date)}
                                                {' '}
                                                {extractTime(eventDate.rescheduled_start_date) === extractTime(eventDate.rescheduled_end_date)
                                                    ? fullDay
                                                    : `${eventTime(eventDate.rescheduled_start_date, eventDate.rescheduled_end_date)} ${clock}`
                                                }
                                            </>}
                                            {eventDate.is_rescheduled && !eventDate.rescheduled_start_date && (
                                                <Translate value="newDateUnknown" />
                                            )}
                                        </span>
                                    </li>
                                )}
                            </ul>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    </>;
};

const DateInfoLoaded = ({ eventDates }) => {
    const startDate = eventDates[0] ? eventDates[0].start_date : false;
    const endDate = eventDates[0] ? eventDates[0].end_date : false;

    const {sameDayEventDates, otherDayEventDates} = useMemo(() => ({
        sameDayEventDates: eventDates.filter(event =>
            (extractDate(event.start_date) === extractDate(startDate)) &&
            (extractDate(event.end_date) === extractDate(endDate))
        ),
        otherDayEventDates: eventDates.filter(event =>
            (extractDate(event.start_date) !== extractDate(startDate)) ||
            (extractDate(event.end_date) !== extractDate(endDate))
        ),
    }), [eventDates, startDate, endDate]);

    return (
        <div className="widget atbd_widget widget-card">
            <div className="atbd_widget_title">
                <h2 className="h4"><span className="la la-clock-o" />&nbsp;<Translate value="date_info" /></h2>
            </div>
            <div className="directory_open_hours">
                {startDate !== false && endDate !== false && (
                    <FirstEventDateInfo
                        startDate={startDate}
                        endDate={endDate}
                        sameDayEventDates={sameDayEventDates}
                    />
                )}
                {(startDate === false || endDate === false) && (
                    <Translate value="no_future_dates" />
                )}
            </div>

            {otherDayEventDates.length > 0 && (
                <OtherDayEventDates otherDayEventDates={otherDayEventDates} />
            )}
        </div>
    );
};

const DateInfo = ({ eventId }) => {
    const { data, isLoading, fetchMore, canFetchMore, isFetchingMore } = useInfiniteEventDates(eventId, {
        end_date_from: 'today',
    });

    // Keep fetching until we loaded all event dates
    useEffect(() => {
        if (canFetchMore && !isFetchingMore) {
            fetchMore();
        }
    }, [fetchMore, canFetchMore, isFetchingMore]);

    if (isLoading) {
        return (
            <div className="widget atbd_widget widget-card">
                <div className="atbd_widget_title">
                    <h2 className="h4">
                        <span className="la la-clock-o" />&nbsp;<Translate value="date_info" />
                    </h2>
                </div>
                <div className="directory_open_hours text-center">
                    <i className="la la-spinner" style={{ animation: 'loader 2s infinite linear' }} />
                </div>
            </div>
        );
    }

    const eventDates = data.reduce((accumulator, currentValue) => [
        ...accumulator,
        ...currentValue.data,
    ], []);

    return <DateInfoLoaded eventDates={eventDates} />;
};

export default DateInfo;
